import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';

function AdminWorkflowScriptAccessManager() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const token = localStorage.getItem('token');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/workflow/script-upload-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load workflow script permissions');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user => [user.email, user.fullName, user.nickname]
      .some(value => value?.toLowerCase().includes(query)));
  }, [search, users]);

  const updatePermission = async (user, allowed) => {
    setSavingId(user.id);
    try {
      await axios.put(`${API_BASE}/workflow/script-upload-users/${user.id}`, { allowed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(current => current.map(item => item.id === user.id
        ? { ...item, canUploadWorkflowScripts: allowed }
        : item));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update workflow script permission');
    } finally {
      setSavingId('');
    }
  };

  return (
    <section style={styles.panel}>
      <p style={styles.explanation}>
        Allow selected users to include blocked runtime files in workflow ZIP uploads and workflow Git commits.
        This does not grant repository write access: add the user to that workflow&apos;s Git Access list separately.
        Malware scanning still runs whenever Malware Scanning is enabled.
      </p>
      <div style={styles.warning}>
        Bypass list: .dll, .exe, .bin, .ps1, .sh, .cmd, .bat, and executable Git file modes.
        Plugin restrictions are unchanged. Framework bundle script access is managed by each bundle&apos;s App Git Access list.
      </div>
      <input
        type="search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search users by email or name"
        style={styles.search}
      />
      {loading ? <p>Loading users...</p> : (
        <div style={styles.list}>
          {visibleUsers.map(user => (
            <label key={user.id} style={styles.row}>
              <span>
                <strong>{user.email || 'No email'}</strong>
                {(user.nickname || user.fullName) && (
                  <span style={styles.name}> {user.nickname || user.fullName}</span>
                )}
              </span>
              <span style={styles.toggleLabel}>
                {savingId === user.id ? 'Saving...' : 'Allow workflow scripts'}
                <input
                  type="checkbox"
                  checked={Boolean(user.canUploadWorkflowScripts)}
                  disabled={savingId === user.id}
                  onChange={event => updatePermission(user, event.target.checked)}
                />
              </span>
            </label>
          ))}
          {visibleUsers.length === 0 && <p>No matching users.</p>}
        </div>
      )}
    </section>
  );
}

const styles = {
  panel: { background: '#fff', border: '1px solid #dce3ef', borderRadius: 10, padding: 20 },
  explanation: { lineHeight: 1.55, marginTop: 0 },
  warning: { background: '#fff7db', border: '1px solid #efd47d', borderRadius: 8, padding: 12, marginBottom: 16 },
  search: { width: '100%', maxWidth: 520, padding: 10, border: '1px solid #cbd5e1', borderRadius: 7, marginBottom: 16 },
  list: { display: 'grid', gap: 8 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 },
  name: { color: '#64748b', marginLeft: 6 },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }
};

export default AdminWorkflowScriptAccessManager;
